import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";

const fmtDue = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtMeeting = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "long", day: "numeric" }) : "");

function FollowUpItem({ f, overdue, onComplete }) {
  return (
    <div data-testid={`na-followup-${f.fu_id}`} className="flex items-center justify-between gap-3 bg-warm-cream border border-ink/10 rounded-lg px-3 py-2.5">
      <div className="min-w-0">
        <div className="text-[15px] font-semibold truncate">{f.entity_name}</div>
        {f.note && <div className="text-[13px] text-ink/60 truncate">{f.note}</div>}
        <div className={`text-[12px] font-semibold mt-0.5 ${overdue ? "text-maroon" : "text-ink/55"}`}>
          {overdue ? "Overdue · " : "Due "}{fmtDue(f.due)}
        </div>
      </div>
      <button data-testid={`na-complete-${f.fu_id}`} onClick={() => onComplete(f.fu_id)} className="shrink-0 border border-dark-olive/40 text-dark-olive rounded-lg px-3 py-2 min-h-[38px] text-[13px] font-semibold">Complete</button>
    </div>
  );
}

// Reusable "Needs your attention" panel — follow-ups assigned to the signed-in
// user plus (optionally) the board-governance summary. Drops into any dashboard.
export default function NeedsAttention({ onNavigate, showGovernance = true }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api.get("/my/needs-attention"); setData(r.data); } catch (e) { setData({ follow_ups: { overdue: [], upcoming: [] }, governance: { show: false } }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const complete = async (fid) => {
    const outcome = window.prompt("Outcome / notes (optional):", "");
    if (outcome === null) return;
    try { await api.post(`/follow-ups/${fid}/complete`, { outcome }); toast.success("Marked done"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not complete"); }
  };
  const toggleTask = async (tid) => {
    try { await api.post(`/board-tasks/${tid}/toggle`); toast.success("Task completed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update task"); }
  };
  const go = (id) => onNavigate && onNavigate(id);

  if (!data) return null;
  const { overdue, upcoming } = data.follow_ups;
  const gov = data.governance || {};
  const govVisible = showGovernance && gov.show;
  const myTasks = (data.tasks?.items) || [];
  const nothing = overdue.length === 0 && upcoming.length === 0 && !govVisible && myTasks.length === 0;

  return (
    <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid="needs-attention">
      <div className="flex items-center gap-2 mb-3">
        <Icon name="Bell" className="w-5 h-5 text-maroon" />
        <h2 className="font-zilla text-xl font-semibold">Needs your attention</h2>
      </div>

      {nothing && (
        <div className="flex items-center gap-2 text-ink/60 text-[15px]" data-testid="na-all-clear">
          <Icon name="Check" className="w-5 h-5 text-olive" /> You're all caught up — nothing needs you right now.
        </div>
      )}

      {govVisible && (
        <div className="mb-4" data-testid="na-governance">
          {gov.next_meeting && (
            <button data-testid="na-next-meeting" onClick={() => { if (gov.next_meeting.meeting_id) sessionStorage.setItem("rca_open_meeting", gov.next_meeting.meeting_id); go("board_meetings"); }} className="w-full text-left rounded-xl p-4 bg-ink text-cream mb-3 hover:bg-ink/90 transition-colors">
              <div className="text-[12px] uppercase tracking-widest text-gold font-bold">Next board meeting</div>
              <div className="font-zilla text-lg font-semibold mt-1">{gov.next_meeting.title || fmtMeeting(gov.next_meeting.meeting_date)}</div>
              {gov.next_meeting.title && <div className="text-cream/75 text-[14px]">{fmtMeeting(gov.next_meeting.meeting_date)}</div>}
            </button>
          )}
          <button data-testid="na-votes-tile" onClick={() => go("votes_approvals")} className="w-full text-left bg-warm-cream border border-ink/15 rounded-xl p-3 hover:border-maroon/50 transition-colors">
            <Icon name="CheckSquare" className="w-4 h-4 text-maroon mb-1" />
            <div className="text-[12px] uppercase tracking-wide text-ink/55 font-semibold">Votes needed</div>
            <div className="font-zilla text-2xl font-semibold">{gov.pending_votes}</div>
          </button>
        </div>
      )}

      {myTasks.length > 0 && (
        <div className="mb-3" data-testid="na-my-tasks">
          <div className="text-[13px] font-semibold text-ink/60 mb-1.5">Tasks assigned to you ({myTasks.length})</div>
          <div className="space-y-1.5">
            {myTasks.map((t) => (
              <div key={t.task_id} data-testid={`na-task-${t.task_id}`} className="flex items-center justify-between gap-3 bg-warm-cream border border-ink/10 rounded-lg px-3 py-2.5">
                <div className="min-w-0">
                  <div className="text-[15px] font-semibold truncate">{t.title}</div>
                  <div className="text-[12px] text-ink/55">{t.source === "minutes" ? "From a board meeting" : "Directly assigned"}</div>
                </div>
                <button data-testid={`na-task-complete-${t.task_id}`} onClick={() => toggleTask(t.task_id)} className="shrink-0 border border-dark-olive/40 text-dark-olive rounded-lg px-3 py-2 min-h-[38px] text-[13px] font-semibold">Complete</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {overdue.length > 0 && (
        <div className="mb-3" data-testid="na-overdue">
          <div className="text-[13px] font-semibold text-maroon mb-1.5">Overdue follow-ups ({overdue.length})</div>
          <div className="space-y-1.5">{overdue.map((f) => <FollowUpItem key={f.fu_id} f={f} overdue onComplete={complete} />)}</div>
        </div>
      )}
      {upcoming.length > 0 && (
        <div data-testid="na-upcoming">
          <div className="text-[13px] font-semibold text-ink/60 mb-1.5">Upcoming follow-ups ({upcoming.length})</div>
          <div className="space-y-1.5">{upcoming.map((f) => <FollowUpItem key={f.fu_id} f={f} onComplete={complete} />)}</div>
        </div>
      )}
    </div>
  );
}
